import ApiService from '@/services/apiService';
import { mapState } from 'vuex';
import { isArray, get, compact, cloneDeep, isEmpty } from 'lodash';
export default {
  name: 'CoreDtwinRuleEditCommands',
  props: {
    dtwin: {
      type: Object,
      required: false,
      default: () => ({})
    },
    rule: {
      type: Object,
      required: true
    },
    // offer picking states in rule-template next to metrics
    states: {
      type: Array,
      required: true
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    hasTwinEditPermission: {
      type: Boolean,
      required: false,
      default: false
    },
    formValid: {
      type: Boolean,
      required: false,
      default: false
    },
    template: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  data: function () {
    return {
      executionResultPrompt: false,
      executionInProgress: false,
      lastExecutionResult: null,
      textboxFields: ['payload', 'html', 'text', 'frm_payload'],
      commands: []
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme']),
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    requiredRule() {
      return [v => !!v || this.$i18n_t('Common.fieldRequired')];
    },
    printedMetricTemplates() {
      return (get(this.rule, 'properties.stateMetrics') || []).map(metric => `{{ ${metric.key} }}`).join(', ');
    },
    printedCustomKVTemplates() {
      return Object.keys(get(this.dtwin, 'data.keyValue') || {}).map(key => `{{ ${key} }}`).join(', ');
    },
    twinType() {
      return get(this.dtwin, 'properties.type');
    },
    getColumnWidth() {
      return (param, paramConfig) => {
        if (paramConfig.width && ['m', 'l'].indexOf(paramConfig.width) >= 0) {
          return param.width === 'm' ? 6 : 12;
        }
        return this.textboxFields.includes(param) ? 12 : 4;
      };
    },
    isTemplateContext() {
      return !isEmpty(this.template);
    }
  },
  watch: {
    rule() {
      this.processRuleActions();
    }
  },
  mounted() {
    this.fetchBridges().then(() => {
      this.processRuleActions();

      // clear incorrect parameters properties from actions (params should match command manifest definition)
      this.rule.properties.actions.forEach(action => {
        const definedParamsKeys = Object.keys(this.parametersForCommandId(action.command));
        for (const key in action.params) {
          if (!definedParamsKeys.includes(key)) {
            delete action.params[key];
          }
        }
      });
    }).catch(error => {
      console.error('Error fetching bridges', error);
    });
  },
  methods: {
    removeTemplateState(stateId) {
      this.rule.properties.actionTemplateStateIds.splice(this.rule.properties.actionTemplateStateIds.indexOf(stateId), 1);
      this.rule.properties.actionTemplateStateIds = [...this.rule.properties.actionTemplateStateIds];
    },
    refreshTemplateStates() {
      this.rule.properties = cloneDeep(this.rule.properties);
    },
    commandChange(index) {
      const changedAction = this.rule.properties.actions[index];
      const newParamsKeys = Object.keys(this.parametersForCommandId(changedAction.command));
      const currentParamsKeys = Object.keys(changedAction.params);
      const keysToRemove = currentParamsKeys.filter(key => !newParamsKeys.includes(key));
      keysToRemove.forEach(key => {
        delete this.rule.properties.actions[index].params[key];
      });
      const bridgeConfig = this.findByCommandId(changedAction.command).config;
      this.$emit('command-changed', index);
      if (bridgeConfig.service === 'Outlook365(OAuth2.0)') {
        this.rule.properties.actions[index].params.from = bridgeConfig.user;
      }
    },
    processRuleActions() {
      if (!isArray(this.rule.properties.actionTemplateStateIds)) {
        this.rule.properties.actionTemplateStateIds = [];
      }
      // change boolean values to string "true"/"false"
      // so they are selectable in the v-select
      // the XAPI offers/changes these trigger-values to proper booleans, but the v-select only has string-values
      // this part maps "hack" maps everything back - the rule-trigger handles both cases anyway, and the XAPI maps them back to boolean
      const conditionsDisabled = this.rule.properties.conditionEnabled === false || this.rule.properties.timeTriggerOnly;
      this.rule.properties.actions.forEach(action => {
        action.triggerValue = conditionsDisabled ? 'true' : `${action.triggerValue || true}`.toLowerCase();
      });
    },
    formattedTemplateState(state) {
      return `{{state:${state.key}}}`;
    },
    formattedVariable(metric) {
      return `{{${metric.key}}}`;
    },
    fetchBridges() {
      return Promise.all([ApiService.getBridgeTypes().then(bridgeTypes => {
        return bridgeTypes.filter(type => {
          // only display bridges that have instance commands
          return Object.keys(get(type, 'manifest.commands.instance', {})).length > 0;
        });
      }), ApiService.getBridgesForTenant(this.isTemplateContext ? this.template.tenantId : this.dtwin.tenantId)]).then(results => {
        let commands = [];
        results[1].forEach(bridgeInstance => {
          const bridgeTypeDef = results[0].find(bridgeDef => bridgeDef.type === bridgeInstance.type);
          const instanceCmds = get(bridgeTypeDef, 'manifest.commands.instance', {});
          commands = commands.concat(Object.keys(instanceCmds).map(commandName => {
            if (instanceCmds[commandName].internal) {
              return null;
            }
            return {
              ...instanceCmds[commandName],
              name: `${bridgeInstance.name}.${commandName}`,
              id: `${bridgeInstance.id}.${commandName}`,
              bridgeType: bridgeInstance.type,
              config: get(bridgeInstance, 'bridge.config', {}),
              configId: bridgeInstance.id,
              manifest: bridgeTypeDef.manifest
            };
          }));
        });
        this.commands = compact(commands).filter(b => {
          const type = results[0].find(t => t.type === b.bridgeType);
          return get(type, 'manifest.functionScope.invokeDirection.outbound', false);
        });
      });
    },
    findByCommandId(commandId) {
      return this.commands.find(c => c.id === commandId);
    },
    orderedParamKeysForCommandId(commandId) {
      const parameters = this.parametersForCommandId(commandId);
      return Object.keys(parameters).sort((a, b) => {
        const orderIdxA = parameters[a].orderIdx || 0;
        const orderIdxB = parameters[b].orderIdx || 0;
        if (orderIdxA < orderIdxB) {
          return -1;
        }
        if (orderIdxA > orderIdxB) {
          return 1;
        }
        return a.localeCompare(b);
      });
    },
    parametersForCommandId(commandId) {
      return get(this.findByCommandId(commandId), 'parameters', {});
    },
    testExecuteAction(index, action) {
      this.executionInProgress = true;
      ApiService.digitalTwinRuleExecuteAction(this, {
        action,
        ruleObj: JSON.parse(JSON.stringify({
          ...this.rule,
          dtwinId: this.isTemplateContext ? null : this.dtwin.id
        })),
        templateId: this.isTemplateContext ? this.template.id : null
      }).then(result => {
        this.executionInProgress = false;
        this.lastExecutionResult = result;
        this.$snack(this.$i18n_t('DigitalTwin.DigitalTwin.ruleModeler.executionSuccessful'), 'success', {
          message: this.$i18n_t('Common.viewDetails'),
          click: () => {
            this.executionResultPrompt = true;
          }
        });
      }).catch(error => {
        this.executionInProgress = false;
        const errorRespData = get(error, 'response.data.data');
        const errorTranslation = errorRespData ? this.$i18n_t(`Error.${errorRespData}`) : null;
        let errorDetails = errorRespData && errorTranslation !== `Error.${errorRespData}` ? errorTranslation : get(error, 'response.data.data');
        if (typeof errorDetails === 'object') {
          errorDetails = JSON.stringify(errorDetails);
        }
        this.lastExecutionResult = get(error, 'response.data.message') ? `${error.response.data.message}: ${errorDetails}` : error.toString();
        this.$snack(this.$i18n_t('DigitalTwin.DigitalTwin.ruleModeler.executionFailed'), 'error', {
          message: this.$i18n_t('Common.viewDetails'),
          click: () => {
            this.executionResultPrompt = true;
          }
        });
      });
    },
    getStateKey(item) {
      if (this.isTemplateContext) {
        return (this.states.find(s => s.key === item) || {}).key;
      } else {
        return (this.states.find(s => s.id === item) || {}).key;
      }
    }
  }
};